import { AxiosInstance } from 'axios';
import { ErrorInterpretationDto, ErrorOwner, ErrorRelationState } from './api';
import { day } from './trace';

/**
 * The dossier of one id - an invoice, a payment or chargeback transaction, a credit note or
 * a customer: the objects as a tree, what happened to them as one list of moments, and the
 * next steps per owner. Mirrors DossierDto on the server.
 */

export type DossierKind = 'Invoice' | 'Customer' | 'Object';

export type DossierRole =
    | 'Other'
    | 'Customer'
    | 'Invoice'
    | 'CreditNote'
    | 'Payment'
    | 'Chargeback'
    | 'Refund'
    | 'Subscription'
    | 'Line'
    | 'Article';

/** The roles that describe a document rather than being objects that can fail. */
export const DESCRIPTIVE_ROLES: DossierRole[] = ['Subscription', 'Line'];

/**
 * One fact about a node: from the document, a relation, or what the connector works out at
 * send time without storing it.
 */
export interface DossierDetail {
    label: string;
    value: string;
    source?: string | null;
    /** Worked out from the settings as they are now; they may have changed since. */
    derived: boolean;
    /** Points at a likely cause. */
    warning: boolean;
}

export type DossierMomentKind =
    | 'Note'
    | 'Failed'
    | 'Succeeded'
    | 'Requeued'
    | 'Removed';

export interface DossierNode {
    key: string;
    parentKey?: string | null;
    role: DossierRole;
    objectType: string;
    relationId: string;
    linkedBy?: string | null;
    /** False when the tie to the parent is likely rather than recorded. */
    linkCertain: boolean;
    exactState: ErrorRelationState;
    dependingId?: string | null;
    entryNumber?: string | null;
    failedAttempts: number;
    firstSeenAt?: string | null;
    lastSeenAt?: string | null;
    interpretation?: ErrorInterpretationDto | null;
    chargebeeNote?: string | null;
    details?: DossierDetail[];
}

export interface DossierMoment {
    id: string;
    nodeKey: string;
    kind: DossierMomentKind;
    headline: string;
    detail?: string | null;
    count: number;
    firstAt?: string | null;
    lastAt?: string | null;
    occurrences: string[];
    raw?: string | null;
    errorLocation?: string | null;
    derived: boolean;
    interpretation?: ErrorInterpretationDto | null;
}

export interface DossierAction {
    code: string;
    owner: ErrorOwner;
    title: string;
    action?: string | null;
    steps: string[];
    details: string[];
    requeueHelps: boolean;
    nodeKeys: string[];
}

/** Whether Chargebee and Exact agree on what is open on an invoice. */
export type DossierSettlementVerdict =
    | 'Unknown'
    | 'InStep'
    | 'WasOutOfStep'
    | 'OutOfStep'
    | 'NotMatchedYet';

export type DossierSettlementSide = 'Chargebee' | 'Exact';

/** One thing that happened to an invoice's money: booked, paid, credited, matched. */
/** What kind of thing happened to the money, so each kind is drawn and explained the same way. */
export type DossierSettlementEventKind =
    | 'Other'
    | 'Invoice'
    | 'SalesEntry'
    | 'BankPayment'
    | 'BankRefund'
    | 'Payment'
    | 'CreditNote'
    | 'CreditNoteBooked'
    | 'Matched'
    | 'PartlyMatched'
    | 'Chargeback'
    | 'BankRemoved';

export interface DossierSettlementEvent {
    at?: string | null;
    /** The moment is a day without a time: Exact dates bank lines and matches by day. */
    dayOnly?: boolean;
    side: DossierSettlementSide;
    kind?: DossierSettlementEventKind;
    /** The connector did this: booked it in Exact, or recorded it in Chargebee from Exact. */
    byConnector?: boolean;
    /** A bank payment or credit note in Exact that no match of the invoice accounts for. */
    unmatched?: boolean;
    /** Where it belongs in time; differs from `at` for a day without a time. */
    sortAt?: string | null;
    /** A payment or bank entry undone later by a chargeback: it happened, but no longer counts. */
    undone?: boolean;
    title: string;
    amount?: number | null;
    reference?: string | null;
    note?: string | null;
    /** This event is where it went wrong. */
    warning: boolean;
}

export interface DossierSettlementFinding {
    text: string;
    warning: boolean;
}

/** How one invoice was settled, in Chargebee and in Exact, side by side. */
/** One item of the invoice in Exact: the open item, or a part Exact split off when part of it was matched. */
export interface DossierExactItem {
    id?: string | null;
    /** What is still open; Exact gives a debit as negative here. */
    open?: number | null;
    original?: number | null;
    journal?: string | null;
    entryNumber?: number | null;
    description?: string | null;
    matched: boolean;
    matchedOn?: string | null;
}

export interface DossierSettlement {
    invoiceKey: string;
    invoiceId: string;
    total?: number | null;
    chargebeeStatus?: string | null;
    /** Null when Chargebee was not asked or did not answer. */
    openInChargebee?: number | null;
    /** Null when Exact was not asked or did not answer. */
    openInExact?: number | null;
    paidInChargebee?: number | null;
    /** What adjustment credit notes took off the invoice. */
    adjustedInChargebee?: number | null;
    creditedInChargebee?: number | null;
    writtenOffInChargebee?: number | null;
    /** The invoice's items in Exact's receivables, matched ones included. */
    exactItems?: DossierExactItem[];
    /** Whether Exact answered; with no items the invoice is then not in Exact at all. */
    exactRead?: boolean;
    /** When Chargebee and Exact were read for this. */
    readAt?: string | null;
    verdict: DossierSettlementVerdict;
    summary: string;
    findings: DossierSettlementFinding[];
    events: DossierSettlementEvent[];
}

export interface Dossier {
    customer: string;
    environment: string;
    query: string;
    kind: DossierKind;
    rootId: string;
    resolvedAs?: string | null;
    focusKey?: string | null;
    from: string;
    to: string;
    askedChargebee: boolean;
    askedExact: boolean;
    conclusion: string;
    actions: DossierAction[];
    nodes: DossierNode[];
    moments: DossierMoment[];
    /** Per invoice, how Chargebee and Exact see it settled; only when they were consulted. */
    settlements?: DossierSettlement[];
    gaps: string[];
}

export interface DossierRequest {
    /** Any number: invoice, transaction, credit note or customer. */
    id: string;
    from?: Date | null;
    to?: Date | null;
    /** Also ask Chargebee which payments it has on the invoice. */
    chargebee?: boolean;
    /** Also ask Exact how it sees the invoice settled. */
    exact?: boolean;
}

export const getDossier = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    request: DossierRequest,
    signal?: AbortSignal
): Promise<Dossier> => {
    const params = new URLSearchParams();

    params.set('id', request.id);

    if (request.from) {
        params.set('from', day(request.from));
    }

    if (request.to) {
        params.set('to', day(request.to));
    }

    if (request.chargebee) {
        params.set('chargebee', 'true');
    }

    if (request.exact) {
        params.set('exact', 'true');
    }

    const response = await axios.get(
        `/Trace/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/dossier?${params.toString()}`,
        { signal }
    );

    // The generated client's axios instance hands the body back as text.
    return (
        typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data
    ) as Dossier;
};

export const ROLE_LABEL: Record<DossierRole, string> = {
    Customer: 'Klant',
    Invoice: 'Factuur',
    CreditNote: 'Creditnota',
    Payment: 'Betaling',
    Chargeback: 'Stornering',
    Refund: 'Terugbetaling',
    Subscription: 'Abonnement',
    Line: 'Regel',
    Article: 'Artikel',
    Other: 'Object',
};
